/**
 * Local-first mutations for SCHEDULE and ATTENDANCE (F3-A).
 *
 * They write to PowerSync → write-path (`db/sync.rs`), which applies the
 * authz (cohort manager: ManageAcademics with campus scoping or lead teacher;
 * attendance is recorded by a cohort teacher) and the HARD validation of
 * schedule conflicts. The denormalized organization_id/campus_id are
 * server-authoritative (filled in here for local optimism).
 *
 * NOT local-first (they go through REST, not here): bulk session generation,
 * student check-in, opening/closing code, report and acknowledging alerts.
 */

import {
  attendance_record,
  class_session,
  eq,
  type PowerSyncDrizzleDB,
  schedule_slot,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  withScope,
} from "../../powersync/verified-write";

export interface SlotContext {
  cohortId: string;
  organizationId: string;
  campusId?: string | null;
}

// ─── Schedule slots ──────────────────────────────────────────────────────

export async function createSlotMutation(
  db: PowerSyncDrizzleDB,
  ctx: SlotContext,
  params: {
    weekday: number;
    startTime: string; // HH:MM
    endTime: string;
    roomId?: string | null;
    teacherId?: string | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(schedule_slot).values({
    id,
    cohort_id: ctx.cohortId,
    organization_id: ctx.organizationId,
    campus_id: ctx.campusId ?? null,
    room_id: params.roomId ?? null,
    weekday: params.weekday,
    start_time: normalizeTime(params.startTime),
    end_time: normalizeTime(params.endTime),
    teacher_id: params.teacherId ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** ⚠️ Dead code: no callers in `apps/web` (no UI to edit a slot). */
export async function updateSlotMutation(
  db: PowerSyncDrizzleDB,
  slotId: string,
  updates: Partial<{
    weekday: number;
    start_time: string;
    end_time: string;
    room_id: string | null;
    teacher_id: string | null;
  }>,
): Promise<void> {
  const patch: Record<string, unknown> = { ...updates, updated_at: nowISO() };
  if (typeof updates.start_time === "string") {
    patch.start_time = normalizeTime(updates.start_time);
  }
  if (typeof updates.end_time === "string") {
    patch.end_time = normalizeTime(updates.end_time);
  }
  await db.update(schedule_slot).set(patch).where(eq(schedule_slot.id, slotId));
}

/**
 * `organizationId`: the org the caller ALREADY has in context (that of the
 * cohort whose schedule tab is open) — deleting slots is `ManageAcademics`
 * (organization capability, see file header), so the organization IS the
 * real boundary here (unlike the review queue in `tutoring/mutations.ts`,
 * which aggregates several cohorts/orgs at once with no single context of
 * its own).
 */
export async function deleteSlotMutation(
  db: PowerSyncDrizzleDB,
  slotId: string,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    schedule_slot,
    schedule_slot.id,
    slotId,
    { deleted_at: nowISO() },
    withScope(options, [{ column: schedule_slot.organization_id, value: organizationId }]),
  );
}

// ─── Class sessions ───────────────────────────────────────────────────────

export async function createSessionMutation(
  db: PowerSyncDrizzleDB,
  ctx: SlotContext,
  params: {
    date: string; // YYYY-MM-DD
    startTime: string;
    endTime: string;
    roomId?: string | null;
    teacherId?: string | null;
    topic?: string | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(class_session).values({
    id,
    cohort_id: ctx.cohortId,
    organization_id: ctx.organizationId,
    campus_id: ctx.campusId ?? null,
    room_id: params.roomId ?? null,
    teacher_id: params.teacherId ?? null,
    date: params.date,
    start_time: normalizeTime(params.startTime),
    end_time: normalizeTime(params.endTime),
    status: "scheduled",
    topic: params.topic ?? null,
    checkin_open: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** ⚠️ Dead code: no callers in `apps/web` (no UI to edit a session). */
export async function updateSessionMutation(
  db: PowerSyncDrizzleDB,
  sessionId: string,
  updates: Partial<{
    date: string;
    start_time: string;
    end_time: string;
    room_id: string | null;
    teacher_id: string | null;
    topic: string | null;
    notes: string | null;
    status: string;
  }>,
): Promise<void> {
  const patch: Record<string, unknown> = { ...updates, updated_at: nowISO() };
  if (typeof updates.start_time === "string") {
    patch.start_time = normalizeTime(updates.start_time);
  }
  if (typeof updates.end_time === "string") {
    patch.end_time = normalizeTime(updates.end_time);
  }
  await db.update(class_session).set(patch).where(eq(class_session.id, sessionId));
}

/** ⚠️ Dead code: no callers in `apps/web` (no UI to delete a session). */
export async function deleteSessionMutation(
  db: PowerSyncDrizzleDB,
  sessionId: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(class_session)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(class_session.id, sessionId));
}

// ─── Attendance (teacher roll call, teacher_roll) ───────────────────

export type AttendanceStatus = "present" | "late" | "absent" | "excused";

/**
 * Records/updates a student's attendance to a session. The write-path forces
 * method='teacher_roll', recorded_by=actor, and recomputes stats/alerts.
 * `existingId` = previous row to update; absent = new entry.
 */
export async function setAttendanceMutation(
  db: PowerSyncDrizzleDB,
  ctx: { sessionId: string; cohortId: string; organizationId: string; campusId?: string | null },
  params: { userId: string; status: AttendanceStatus; note?: string | null; existingId?: string },
  options?: VerifiedWriteOptions,
): Promise<string> {
  const now = nowISO();
  if (params.existingId) {
    // The caller ALREADY knows there's a row (`existingId`) — the pattern
    // that avoids Class 2 (POWERSYNC_VERIFIED_WRITE.md §3, "split the upsert"):
    // no deciding SELECT, only the UPDATE by PK that patchRowVerified can shield.
    //
    // `scope` by `ctx.cohortId` (not `organizationId`, even though `ctx`
    // already carries it too): attendance is recorded by "a teacher OF THE
    // COHORT" (see file header), a cohort boundary, not an organization
    // capability — `cohort_id` is the most precise filter that reflects that
    // boundary, at no extra plumbing cost (`ctx` already carries it).
    await patchRowVerified(
      db,
      attendance_record,
      attendance_record.id,
      params.existingId,
      { status: params.status, note: params.note ?? null },
      withScope(options, [{ column: attendance_record.cohort_id, value: ctx.cohortId }]),
    );
    return params.existingId;
  }
  const id = generateId();
  await db.insert(attendance_record).values({
    id,
    class_session_id: ctx.sessionId,
    cohort_id: ctx.cohortId,
    organization_id: ctx.organizationId,
    campus_id: ctx.campusId ?? null,
    user_id: params.userId,
    status: params.status,
    method: "teacher_roll",
    recorded_at: now,
    note: params.note ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Normalizes `HH:MM` → `HH:MM:SS` (the server's time columns expect seconds). */
function normalizeTime(t: string): string {
  const trimmed = t.trim();
  return /^\d{2}:\d{2}$/.test(trimmed) ? `${trimmed}:00` : trimmed;
}
